import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { isEqual } from 'lodash-es'
import { from, of } from 'rxjs'
import { catchError, distinctUntilChanged, groupBy, map, mergeMap, switchMap, tap } from 'rxjs/operators'
import { TOP_LEVEL_FILTERS } from 'src/components/invoices/invoices-list/invoices-list.filters'
import { InvoiceElasticSearchService } from 'src/services/data/invoice-elastic.service'
import { InvoicesService } from 'src/services/data/invoices.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { prepareFiltersPayload, prepareInvoicesPayload } from './invoice-view-filters.utils'
import { loadInvoiceFilters, loadInvoiceFiltersFailure, loadInvoiceFiltersSuccess, loadInvoiceViews, loadInvoiceViewsFailure, loadInvoiceViewsSuccess, setInvoiceInv, setInvoiceInvFailure, setInvoiceInvSuccess, setInvoicePaidDate, setInvoicePaidDateFailure, setInvoicePaidDateSuccess, setInvoicePaymentType, setInvoicePaymentTypeFailure, setInvoicePaymentTypeSuccess } from './invoice-view.actions'


@Injectable()
export class InvoiceViewEffects {
  private actions$ = inject(Actions)
  private elastic = inject(InvoiceElasticSearchService)
  private toaster = inject(ToasterService)
  private invoices = inject(InvoicesService)


  loadInvoiceViews$ = createEffect(() => this.actions$.pipe(
    ofType(loadInvoiceViews),
    groupBy(action => {
      const payload = prepareInvoicesPayload(action.filters, 0)
      const { skip, ...querySort } = payload
      return `${action.tableKey}\u0000${JSON.stringify(querySort)}`
    }),
    mergeMap(group$ => group$.pipe(
      switchMap(action => {
        const payload = prepareInvoicesPayload(action.filters, action.page)
        return this.elastic.fetchInvoices({ ...payload, calculate: action.calculate }).pipe(
          map(r => loadInvoiceViewsSuccess({
            tableKey: action.tableKey,
            total: r.total,
            totals: r.totals,
            invoiceViews: r.data,
            payload,
            resetTable: action.resetTable,
          })),
          catchAndLog(this.toaster, 'Unable to fetch invoices', loadInvoiceViewsFailure)
        )
      })
    ))
  ))

  loadInvoiceFilters$ = createEffect(() => this.actions$.pipe(
    ofType(loadInvoiceFilters),
    map(action => ({
      tableKey: action.tableKey,
      payload: prepareFiltersPayload(action.filters, TOP_LEVEL_FILTERS[action.tableKey]),
    })),
    distinctUntilChanged(isEqual),
    switchMap(({tableKey, payload}) => this.elastic.fetchInvoiceFilters(payload).pipe(
      map(result => loadInvoiceFiltersSuccess({ tableKey, result })),
      catchError(error => of(loadInvoiceFiltersFailure({ error }))),
    ))))

  setInvoiceInv$ = createEffect(() => this.actions$.pipe(
    ofType(setInvoiceInv),
    tap(() => this.toaster.progress('Changing invoice "INV" value...')),
    switchMap(action =>
      from(this.invoices.patchImmutableInvoice(action.invoice, { inv: action.inv })).pipe(
        tap(() => this.toaster.success('Invoice "INV" value changed successfully')),
        map(() => setInvoiceInvSuccess()),
        catchAndLog(this.toaster, 'Unable to update invoice "INV" value', setInvoiceInvFailure
      )))))

  setInvoicePaymentType$ = createEffect(() => this.actions$.pipe(
    ofType(setInvoicePaymentType),
    tap(() => this.toaster.progress('Changing invoice "Payment Type" value...')),
    switchMap(action =>
      from(this.invoices.patchImmutableInvoice(action.invoice, { attributes: { payment_type: action.paymentType } })).pipe(
        tap(() => this.toaster.success('Invoice "Payment Type" value changed successfully')),
        map(() => setInvoicePaymentTypeSuccess()),
        catchAndLog(this.toaster, 'Unable to update invoice "Payment Type" value', setInvoicePaymentTypeFailure
      )))))

  setInvoicePaidDate$ = createEffect(() => this.actions$.pipe(
    ofType(setInvoicePaidDate),
    tap(() => this.toaster.progress('Changing invoice "Paid Date" value...')),
    switchMap(action =>
      from(this.invoices.patchImmutableInvoice(action.invoice, { attributes: { paid: action.date } })).pipe(
        tap(() => this.toaster.success('Invoice "Paid Date" changed successfully')),
        map(() => setInvoicePaidDateSuccess()),
        catchAndLog(this.toaster, 'Unable to update invoice "Paid Date"', setInvoicePaidDateFailure
      )))))
}
